/**
 * The story studio's route model (spec 025), shared by the map's two looks
 * (Path and Fly-through). Pure: it lays a story out from its graph alone and
 * never reads `node.position` (the 2D layout belongs to the hood, spec 010).
 *
 * - A depth-first walk from the start marks back edges `loopBack`, so a
 *   "try again" loop never stops the layering.
 * - `layer` is the longest path from the start over the other edges.
 * - Within a layer, nodes are ordered by the mean order of their placed
 *   predecessors; ties go to the predecessor's choice order, then node id.
 * - Scene nodes the start cannot reach are listed in `offPath`.
 *
 * The world placement of the fly-through (`flyPositions`, with `LAYER_DEPTH`,
 * `LANE_WIDTH` and the winding `spineX`) follows the rules of
 * `../treasure-hunt/Editor/src/utils/flyRoute.ts`, which this file replaces.
 */

import type { ChoiceOption, StoryGraph, StorySceneWithScene } from '../../../Shared/src/@types';

export type RouteNodeKind = 'scene' | 'end';

export interface RouteNode {
  id: string;
  kind: RouteNodeKind;
  storySceneId: number;
  /** The scene's name ('' for an end node). */
  name: string;
  layer: number;
  /** Place within its layer, 0-based. */
  order: number;
  /** 1-based place in `stops` (scene nodes on the path only). */
  stop?: number;
}

export interface RouteEdge {
  id: string;
  from: string;
  to: string;
  /** The choice this edge follows, when it has a condition. */
  choiceId?: string;
  /** The choice's text (or the edge's own label) when it has a condition. */
  label?: string;
  /** A back edge of the walk from the start: a loop ("try again"). */
  loopBack: boolean;
}

export interface StoryRoute {
  /** The layered nodes: scenes and end nodes the start reaches. */
  nodes: RouteNode[];
  /** One per graph edge whose ends are both on the graph. */
  edges: RouteEdge[];
  /** At least 1. */
  maxLayer: number;
  /** The scene nodes on the path, by layer then order, numbered from 1. */
  stops: RouteNode[];
  /** Scene nodes the start cannot reach, in graph order. */
  offPath: RouteNode[];
}

type SceneRow = Pick<StorySceneWithScene, 'id' | 'sceneName' | 'choices'>;

export function buildStoryRoute(graph: StoryGraph, storyScenes: readonly SceneRow[]): StoryRoute {
  const rows = new Map(storyScenes.map((ss) => [ss.id, ss]));
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const choicesOf = (nodeId: string): readonly ChoiceOption[] => {
    const n = nodeById.get(nodeId);
    return (n && n.type !== 'end' ? rows.get(n.storySceneId)?.choices : null) ?? [];
  };
  const choiceIndex = (from: string, choiceId: string | undefined): number => {
    if (!choiceId) return Number.MAX_SAFE_INTEGER;
    const i = choicesOf(from).findIndex((c) => c.id === choiceId);
    return i < 0 ? Number.MAX_SAFE_INTEGER - 1 : i;
  };

  const edges: RouteEdge[] = [];
  for (const e of graph.edges) {
    if (!nodeById.has(e.from) || !nodeById.has(e.to)) continue;
    const edge: RouteEdge = { id: e.id, from: e.from, to: e.to, loopBack: false };
    if (e.condition) {
      edge.choiceId = e.condition;
      const text = choicesOf(e.from).find((c) => c.id === e.condition)?.text ?? e.label;
      if (text !== undefined) edge.label = text;
    }
    edges.push(edge);
  }

  // Outgoing edges in the source's choice order (unconditional last), then id.
  const out = new Map<string, RouteEdge[]>(graph.nodes.map((n) => [n.id, []]));
  for (const e of edges) out.get(e.from)!.push(e);
  for (const list of out.values()) {
    list.sort((a, b) => choiceIndex(a.from, a.choiceId) - choiceIndex(b.from, b.choiceId) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }

  // Depth-first walk from the start: back edges are loops.
  const start = nodeById.has(graph.startNodeId) ? graph.startNodeId : null;
  const reached = new Set<string>();
  if (start) {
    const onStack = new Set<string>();
    const stack: Array<{ id: string; next: number }> = [{ id: start, next: 0 }];
    reached.add(start);
    onStack.add(start);
    while (stack.length) {
      const top = stack[stack.length - 1];
      const list = out.get(top.id)!;
      if (top.next >= list.length) {
        onStack.delete(top.id);
        stack.pop();
        continue;
      }
      const e = list[top.next++];
      if (onStack.has(e.to)) {
        e.loopBack = true;
      } else if (!reached.has(e.to)) {
        reached.add(e.to);
        onStack.add(e.to);
        stack.push({ id: e.to, next: 0 });
      }
    }
  }

  // Layers: longest path from the start over the forward edges (Kahn).
  const forward = edges.filter((e) => !e.loopBack && reached.has(e.from) && reached.has(e.to));
  const layer = new Map<string, number>([...reached].map((id) => [id, 0]));
  const indeg = new Map<string, number>([...reached].map((id) => [id, 0]));
  for (const e of forward) indeg.set(e.to, indeg.get(e.to)! + 1);
  const queue = graph.nodes.map((n) => n.id).filter((id) => reached.has(id) && indeg.get(id) === 0);
  while (queue.length) {
    const id = queue.shift()!;
    for (const e of out.get(id)!) {
      if (e.loopBack || !reached.has(e.to)) continue;
      layer.set(e.to, Math.max(layer.get(e.to)!, layer.get(id)! + 1));
      const left = indeg.get(e.to)! - 1;
      indeg.set(e.to, left);
      if (left === 0) queue.push(e.to);
    }
  }

  // Order within each layer.
  const preds = new Map<string, RouteEdge[]>([...reached].map((id) => [id, []]));
  for (const e of forward) preds.get(e.to)!.push(e);
  const byLayer: string[][] = [];
  for (const n of graph.nodes) if (reached.has(n.id)) (byLayer[layer.get(n.id)!] ||= []).push(n.id);
  const order = new Map<string, number>();
  byLayer.forEach((ids) => {
    if (!ids) return;
    const key = new Map<string, { mean: number; choice: number }>();
    for (const id of ids) {
      const placed = preds.get(id)!.filter((e) => order.has(e.from));
      const mean = placed.length ? placed.reduce((s, e) => s + order.get(e.from)!, 0) / placed.length : 0;
      // The tie-break is the choice order on the first-placed predecessor.
      const first = [...placed].sort((a, b) => order.get(a.from)! - order.get(b.from)!)[0];
      key.set(id, { mean, choice: first ? choiceIndex(first.from, first.choiceId) : Number.MAX_SAFE_INTEGER });
    }
    const sorted = [...ids].sort((a, b) => {
      const ka = key.get(a)!;
      const kb = key.get(b)!;
      return ka.mean - kb.mean || ka.choice - kb.choice || (a < b ? -1 : a > b ? 1 : 0);
    });
    sorted.forEach((id, i) => order.set(id, i));
  });

  const toRouteNode = (id: string, l: number, o: number): RouteNode => {
    const n = nodeById.get(id)!;
    const kind: RouteNodeKind = n.type === 'end' ? 'end' : 'scene';
    const row = kind === 'scene' ? rows.get(n.storySceneId) : undefined;
    return {
      id,
      kind,
      storySceneId: n.storySceneId,
      name: kind === 'end' ? '' : row?.sceneName || `Scene #${n.storySceneId}`,
      layer: l,
      order: o,
    };
  };

  const nodes = graph.nodes
    .filter((n) => reached.has(n.id))
    .map((n) => toRouteNode(n.id, layer.get(n.id)!, order.get(n.id)!))
    .sort((a, b) => a.layer - b.layer || a.order - b.order);
  const stops = nodes.filter((n) => n.kind === 'scene');
  stops.forEach((n, i) => { n.stop = i + 1; });
  const offPath = graph.nodes
    .filter((n) => !reached.has(n.id) && n.type !== 'end')
    .map((n) => toRouteNode(n.id, -1, -1));
  const maxLayer = Math.max(1, ...nodes.map((n) => n.layer));

  return { nodes, edges, maxLayer, stops, offPath };
}

// ── The fly-through's world placement ───────────────────────────────────────

/** World depth (z) per route layer (025 D-12: wider than the source's 3.4, so rows separate). */
export const LAYER_DEPTH = 4.5;
/** Sideways distance between the nodes of one layer (025 D-12: the source's 3.2 widened). */
export const LANE_WIDTH = 4;

const SPINE_AMPLITUDE = 3.4;
const SPINE_FREQUENCY = 0.42;

/** The gently winding centre line of the route at `layer` (world x). */
export const spineX = (layer: number): number => SPINE_AMPLITUDE * Math.sin(SPINE_FREQUENCY * layer);
/** d spineX / d layer. */
export const spineDX = (layer: number): number =>
  SPINE_AMPLITUDE * SPINE_FREQUENCY * Math.cos(SPINE_FREQUENCY * layer);

export interface FlyPosition {
  x: number;
  z: number;
}

/**
 * Where each route node stands on the ground: `z = layer × LAYER_DEPTH`, the
 * layer's nodes `LANE_WIDTH` apart around `spineX(layer)` in the route's
 * order, so a choice's branches stand side by side.
 */
export function flyPositions(route: Pick<StoryRoute, 'nodes'>): Map<string, FlyPosition> {
  const counts = new Map<number, number>();
  for (const n of route.nodes) counts.set(n.layer, (counts.get(n.layer) ?? 0) + 1);
  const out = new Map<string, FlyPosition>();
  for (const n of route.nodes) {
    const count = counts.get(n.layer)!;
    out.set(n.id, {
      x: spineX(n.layer) + (n.order - (count - 1) / 2) * LANE_WIDTH,
      z: n.layer * LAYER_DEPTH,
    });
  }
  return out;
}
